/**
 * The hospital admin's account tools, from the QA report's section 3.2:
 *
 *   HospitalBrandingPanel  the hospital's own logo — shown to every one of its
 *                          users beside the platform's marks, never instead —
 *                          and the plate it is drawn on, so a transparent PNG
 *                          does not vanish on a theme the same colour as it
 *   EditAccountModal       change a staff account's name, username, Google
 *                          address, phone or password
 *   ResetPasswordDialog    reset a password to the account's own username,
 *                          and show it so it can be read out over the phone
 *
 * The server decides who may touch which account (lib/hospitalStaff.js
 * staffTargetAllowed): the administrative manager manages the staff but not
 * their peers or the owner. `mayManageStaffRole` mirrors it only to decide
 * which buttons to draw.
 */

import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Field, Icon, Input, Modal, Panel, Select } from '../../components/common';
import { institutionApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { compressImage, formatBytes, dataUriByteLength } from '../../domain/files';
import { MEMBER_ROLE_LABELS } from '../../domain/rbac';
import { LOGO_BACKDROPS, inspectLogo, isLogoBackdrop, logoInkAdvice, logoPlateClass } from '../../domain/logoBackdrop';
import { AdminRolePicker } from './AdminRolePicker';

/* ========================================================================== */
/* The logo                                                                   */
/* ========================================================================== */

/** 512px is sharp on a 2x sidebar and on a printed receipt, and stays small. */
const LOGO_EDGE = 512;

const backdropName = (id) => LOGO_BACKDROPS.find((b) => b.id === id)?.name || '';

/**
 * One plate option, drawn the way it will look: the logo on that plate, over
 * a dark surface and a light one side by side, so a logo that would vanish on
 * one of them is seen vanishing before it is chosen.
 */
export function BackdropOption({ option, logo, active, suggested, saving, index, onPick }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={saving}
      onClick={() => onPick(option.id)}
      className={`theme-card backdrop-card ${active ? 'theme-card--active' : ''}`}
      style={{ '--theme-accent': 'rgb(var(--accent-rgb))', animationDelay: `${index * 70}ms` }}
    >
      <span className="backdrop-stage" aria-hidden="true">
        <span className="backdrop-stage__half backdrop-stage__half--dark">
          <img src={logo} alt="" className={`backdrop-stage__logo ${logoPlateClass(option.id)}`} />
          <small>مظهر داكن</small>
        </span>
        <span className="backdrop-stage__half backdrop-stage__half--light">
          <img src={logo} alt="" className={`backdrop-stage__logo ${logoPlateClass(option.id)}`} />
          <small>مظهر فاتح</small>
        </span>
      </span>
      <span className="flex items-start gap-3 p-3 text-start">
        <span className="theme-card__radio" style={{ borderColor: 'rgb(var(--accent-rgb))', background: active ? 'rgb(var(--accent-rgb))' : 'transparent' }}>
          {saving ? (
            <Icon name="loader-2" size={12} className="animate-spin text-onaccent" />
          ) : (
            active && <Icon name="check" size={13} className="text-onaccent" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <Icon name={option.icon} size={15} className="text-cyan-300" />
            <span className="font-bold text-white">{option.name}</span>
            {suggested && (
              <span className="backdrop-suggested">
                <Icon name="sparkles" size={11} /> مقترح
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-xs text-slate-400">{option.hint}</span>
        </span>
      </span>
    </button>
  );
}

/**
 * المرحلة 1.35 — «الشعار على الثيمات الداكنة»: the same mark drawn for a dark
 * page (light lines), optional. Under a dark theme the sidebar, the header and
 * the quick reception's card show it, and the queue TV always does, instead of
 * the logo on its plate; paper always prints the logo (domain/logoBackdrop.js
 * pickLogo).
 */
function DarkLogo() {
  const { institutionLogoDark, refresh } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const save = async (logoDark) => {
    setBusy(true);
    try {
      await institutionApi.updateMine({ logoDark });
      await refresh();
      success(logoDark ? 'تم حفظ الشعار على الثيمات الداكنة' : 'تمت إزالة الشعار على الثيمات الداكنة');
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const choose = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toastError('اختر صورة (PNG أو JPG أو WebP)');
      return;
    }
    await save(await compressImage(file, { maxWidth: LOGO_EDGE, maxHeight: LOGO_EDGE, mimeType: 'image/png' }));
  };

  return (
    <div className="mt-5 flex flex-wrap items-center gap-5 border-t border-white/5 pt-5">
      <div className="logo-well logo-well--dark" aria-live="polite">
        {institutionLogoDark ? (
          <img src={institutionLogoDark} alt="الشعار على الثيمات الداكنة" className="h-full w-full rounded-2xl object-contain" />
        ) : (
          <Icon name="moon" size={30} className="text-slate-500" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="flex items-center gap-2 text-sm font-bold text-white">
          <Icon name="moon" size={16} className="text-cyan-300" /> الشعار على الثيمات الداكنة <span className="text-xs font-normal text-slate-500">(اختياري)</span>
        </h3>
        <p className="mt-1 text-xs text-slate-400">
          نسخة من الشعار بخطوط فاتحة تظهر مع الثيمات الداكنة وشاشة الأدوار بدل الشعار على خلفيته. الإيصالات المطبوعة تبقى بالشعار الأساسي.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={choose} />
        <Button variant="subtle" icon="upload" loading={busy} onClick={() => input.current?.click()}>
          {institutionLogoDark ? 'تغيير' : 'رفع شعار داكن'}
        </Button>
        {institutionLogoDark && (
          <Button variant="ghost" icon="trash-2" disabled={busy} onClick={() => save(null)}>
            إزالة
          </Button>
        )}
      </div>
    </div>
  );
}

export function HospitalBrandingPanel() {
  const { institutionLogo, institutionLogoBackdrop, institutionName, refresh } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [preview, setPreview] = useState(institutionLogo || null);
  const [backdrop, setBackdrop] = useState(isLogoBackdrop(institutionLogoBackdrop) ? institutionLogoBackdrop : 'none');
  const [reading, setReading] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingBackdrop, setSavingBackdrop] = useState(null);
  const input = useRef(null);

  useEffect(() => setPreview(institutionLogo || null), [institutionLogo]);
  useEffect(() => {
    if (isLogoBackdrop(institutionLogoBackdrop)) setBackdrop(institutionLogoBackdrop);
  }, [institutionLogoBackdrop]);

  // What the logo is drawn in, so the right plate can be suggested — for a
  // logo uploaded before this option existed as much as for a new one.
  useEffect(() => {
    let live = true;
    setReading(null);
    if (preview) inspectLogo(preview).then((r) => live && setReading(r));
    return () => {
      live = false;
    };
  }, [preview]);

  const choose = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toastError('اختر صورة (PNG أو JPG أو WebP)');
      return;
    }
    setBusy(true);
    try {
      // PNG keeps a transparent background transparent — a logo on a white
      // plate looks pasted on in the dark themes.
      const dataUri = await compressImage(file, { maxWidth: LOGO_EDGE, maxHeight: LOGO_EDGE, mimeType: 'image/png' });
      // A new logo gets the plate its own colours call for; the one chosen for
      // the previous logo says nothing about this one.
      const found = await inspectLogo(dataUri);
      const plate = found ? found.suggestion : backdrop;
      await institutionApi.updateMine({ logo: dataUri, logoBackdrop: plate });
      setPreview(dataUri);
      setBackdrop(plate);
      await refresh();
      success(
        plate === 'none'
          ? 'تم حفظ شعار المستشفى — سيظهر لكل مستخدمي المنشأة'
          : `تم حفظ الشعار واخترنا له «${backdropName(plate)}» حتى يبقى واضحاً — يمكنك تغييرها من الأسفل`,
      );
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const pickBackdrop = async (id) => {
    if (id === backdrop || savingBackdrop) return;
    const before = backdrop;
    setBackdrop(id);
    setSavingBackdrop(id);
    try {
      await institutionApi.updateMine({ logoBackdrop: id });
      await refresh();
      success(`تم حفظ خلفية الشعار: ${backdropName(id)}`);
    } catch (err) {
      setBackdrop(before);
      toastError(err.message);
    } finally {
      setSavingBackdrop(null);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await institutionApi.updateMine({ logo: null });
      setPreview(null);
      await refresh();
      success('تمت إزالة الشعار');
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const advice = logoInkAdvice(reading);

  return (
    <Panel className="brand-panel mb-6">
      <div className="flex flex-wrap items-center gap-5">
        <div className="logo-well" aria-live="polite">
          {preview ? (
            <img
              src={preview}
              alt={`شعار ${institutionName || 'المستشفى'}`}
              className={`h-full w-full rounded-2xl object-contain ${logoPlateClass(backdrop)}`}
            />
          ) : (
            <Icon name="image-plus" size={34} className="text-slate-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-base font-bold text-white">
            <Icon name="badge-check" size={18} className="text-cyan-300" /> شعار المستشفى
          </h2>
          <p className="mt-1 text-sm text-slate-400">
            يظهر لكل موظفي {institutionName || 'المنشأة'} بجانب شعارات النظام، وعلى الإيصالات المطبوعة وشاشة الأدوار.
          </p>
          {preview && (
            <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <span>
                الحجم الحالي <span dir="ltr">{formatBytes(dataUriByteLength(preview))}</span>
              </span>
              {reading?.transparent && (
                <span className="rounded-md bg-white/5 px-1.5 py-0.5 text-slate-400">PNG بخلفية شفافة</span>
              )}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={input} type="file" accept="image/*" className="hidden" onChange={choose} />
          <Button icon="upload" loading={busy} onClick={() => input.current?.click()}>
            {preview ? 'تغيير الشعار' : 'رفع الشعار'}
          </Button>
          {preview && (
            <Button variant="ghost" icon="trash-2" disabled={busy} onClick={remove}>
              إزالة
            </Button>
          )}
        </div>
      </div>

      {preview && (
        <div className="mt-5 border-t border-white/5 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-bold text-white">
              <Icon name="contrast" size={16} className="text-cyan-300" /> خلفية الشعار
            </h3>
            {reading && reading.suggestion !== backdrop && (
              <Button variant="subtle" icon="sparkles" disabled={Boolean(savingBackdrop)} onClick={() => pickBackdrop(reading.suggestion)}>
                استخدم المقترح: {backdropName(reading.suggestion)}
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-400">
            إن كانت خطوط الشعار داكنة اختر خلفية فاتحة، وإن كانت فاتحة اختر خلفية داكنة — حتى لا يختفي الشعار مع أي مظهر أو على
            الإيصال المطبوع.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3" role="radiogroup" aria-label="خلفية الشعار">
            {LOGO_BACKDROPS.map((option, index) => (
              <BackdropOption
                key={option.id}
                option={option}
                logo={preview}
                index={index}
                active={option.id === backdrop}
                suggested={reading?.suggestion === option.id && option.id !== 'none'}
                saving={savingBackdrop === option.id}
                onPick={pickBackdrop}
              />
            ))}
          </div>
          {advice && (
            <p className="mt-3 flex items-start gap-2 text-xs text-slate-400">
              <Icon name="lightbulb" size={14} className="mt-0.5 shrink-0 text-amber-300" />
              {advice}
            </p>
          )}
        </div>
      )}

      {preview && <DarkLogo />}
    </Panel>
  );
}

/* ========================================================================== */
/* Editing an account                                                         */
/* ========================================================================== */

export function EditAccountModal({ member, doctors = [], onClose, onSaved }) {
  const { success, error: toastError } = useNotifications();
  const { servicePolicy } = usePermissions();
  const adminRolesOpen = (servicePolicy('adminRoles') || {}).enabled !== 'no';
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!member) return;
    setForm({
      name: member.user?.name || '',
      username: member.user?.username || '',
      email: member.user?.email || '',
      phone: member.user?.phone || '',
      password: '',
      servesDoctorId: member.servesDoctorId ? String(member.servesDoctorId) : '',
      adminRole: member.adminRole || '',
    });
    setShowPassword(false);
  }, [member]);

  if (!member || !form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        username: form.username,
        email: form.email,
        phone: form.phone,
      };
      if (form.password) payload.password = form.password;
      if (member.memberRole === 'secretary' && form.servesDoctorId) payload.servesDoctorId = form.servesDoctorId;
      // المرحلة 1.1: a changed administration role (or back to none).
      if (member.memberRole === 'co_admin' && form.adminRole !== (member.adminRole || '')) payload.adminRole = form.adminRole || null;
      await institutionApi.updateMember(member.id, payload);
      success(form.password ? 'تم حفظ الحساب وكلمة المرور الجديدة' : 'تم حفظ بيانات الحساب');
      onSaved?.();
      onClose();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`تعديل حساب — ${member.user?.name || ''}`}
      icon="user-cog"
      closeOnBackdrop={false}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="save" loading={busy} disabled={!form.name.trim() || !form.username.trim()} onClick={save}>
            حفظ التعديلات
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-200">
            {MEMBER_ROLE_LABELS[member.memberRole] || member.memberRole}
          </Badge>
          {member.user?.mustChangePassword && (
            <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-200" icon="key-round">
              بانتظار تغيير كلمة المرور
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="الاسم" required>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="اسم المستخدم" required hint="يُستخدم للدخول — بدون مسافات">
            <Input dir="ltr" value={form.username} onChange={(e) => set({ username: e.target.value.replace(/\s/g, '') })} />
          </Field>
          <Field label="بريد Google">
            <Input dir="ltr" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
          </Field>
          <Field label="الهاتف">
            <Input dir="ltr" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
        </div>

        <Field
          label="كلمة مرور جديدة"
          hint="اتركها فارغة للإبقاء على الحالية. سيُطلب من الموظف تغييرها عند دخوله التالي."
        >
          <div className="relative">
            <Input
              dir="ltr"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set({ password: e.target.value })}
              className="pe-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 end-0 flex w-11 items-center justify-center text-slate-400 hover:text-cyan-300"
              aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
            >
              <Icon name={showPassword ? 'eye-off' : 'eye'} size={16} />
            </button>
          </div>
        </Field>

        {member.memberRole === 'co_admin' && adminRolesOpen && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <AdminRolePicker value={form.adminRole} onChange={(key) => set({ adminRole: key })} />
          </div>
        )}

        {member.memberRole === 'secretary' && (
          <Field label="الطبيب الذي يعمل معه">
            <Select value={form.servesDoctorId} onChange={(e) => set({ servesDoctorId: e.target.value })}>
              <option value="">اختر الطبيب…</option>
              {doctors.map((d) => (
                <option key={d.userId} value={d.userId}>
                  {d.user?.name || `#${d.userId}`}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
    </Modal>
  );
}

/* ========================================================================== */
/* Reset to the username                                                      */
/* ========================================================================== */

export function ResetPasswordDialog({ member, onClose, onDone }) {
  const { success, error: toastError } = useNotifications();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setResult(null);
    setCopied(false);
  }, [member]);

  if (!member) return null;

  const reset = async () => {
    setBusy(true);
    try {
      const r = await institutionApi.resetMemberPassword(member.id);
      setResult(r);
      success('تمت إعادة تعيين كلمة المرور');
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* a refused clipboard is not worth an error — the password is on screen */
    }
  };

  // The roster reloads when the dialog closes, not when the reset lands: a
  // reload re-renders the screen behind the dialog, and the temporary password
  // has to stay on screen until the admin has read it out.
  const close = () => {
    if (result) onDone?.();
    onClose();
  };

  return (
    <Modal
      open
      onClose={close}
      title="إعادة تعيين كلمة المرور"
      icon="key-round"
      size="sm"
      footer={
        result ? (
          <div className="flex justify-end">
            <Button onClick={close}>تم</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              إلغاء
            </Button>
            <Button variant="danger" icon="rotate-ccw" loading={busy} onClick={reset}>
              إعادة التعيين
            </Button>
          </div>
        )
      }
    >
      {result ? (
        <div className="space-y-4 text-center">
          <span className="tile3d__face tone-emerald mx-auto !h-14 !w-14" aria-hidden="true">
            <Icon name="shield-check" size={26} className="tile3d__icon" />
          </span>
          <p className="text-sm text-slate-300">
            كلمة المرور المؤقتة لحساب <b className="text-white">{member.user?.name}</b> أصبحت اسم المستخدم:
          </p>
          <div className="temp-password" dir="ltr">
            {result.temporaryPassword}
          </div>
          <Button variant="ghost" icon={copied ? 'check' : 'copy'} onClick={copy}>
            {copied ? 'تم النسخ' : 'نسخ'}
          </Button>
          <p className="text-xs text-amber-200">سيُطلب من الموظف اختيار كلمة مرور جديدة عند أول دخول.</p>
        </div>
      ) : (
        <div className="space-y-3 text-sm text-slate-300">
          <p>
            ستصبح كلمة مرور <b className="text-white">{member.user?.name}</b> هي اسم المستخدم نفسه:
          </p>
          <p className="rounded-xl bg-white/5 p-3 text-center font-mono text-cyan-200" dir="ltr">
            {member.user?.username}
          </p>
          <p className="text-xs text-slate-400">كلمة المرور الحالية ستتوقف فوراً، وسيُطلب من الموظف تغيير المؤقتة عند دخوله.</p>
        </div>
      )}
    </Modal>
  );
}
